import * as React from 'react';
import {
  DateField,
  Show,
  Labeled,
  NumberField,
  PrevNextButtons,
  ReferenceField,
  TextField,
  Toolbar,
  useRecordContext,
  useTranslate,
  SaveButton,
  FunctionField,
  Link,
  useShowController,
  useNotify,
  useRefresh,
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Grid,
  Typography,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import RefundIcon from '@mui/icons-material/CurrencyExchange';
import RetryIcon from '@mui/icons-material/Refresh';
import { apiUrl } from '../constants';

import { Order, Customer } from '../types';
import { renderPaymentStatus } from './BillpayOrderList';
import { CustomerDetails } from '../orders/OrderEdit';
import { useState } from 'react';

const OrderEdit = () => (
  <Show title={<OrderTitle />} component='div'>
    <OrderForm />
  </Show>
);

const OrderTitle = () => {
  const translate = useTranslate();
  const record = useRecordContext<Order>();
  return record ? (
    <span>
      {translate('resources.commands.title', {
        reference: record.id,
      })}
    </span>
  ) : null;
};

const OrderForm = () => {
  const { record } = useShowController();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loadingAction, setLoadingAction] = useState<'refund' | 'retry' | null>(
    null
  );
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'refund' | 'retry' | null;
  }>({ open: false, action: null });

  const isRetryable =
    record?.paymentStatus.toLowerCase() === 'received' &&
    record?.billPayStatus.toLowerCase() === 'failed';

  const handleConfirmAction = async () => {
    if (!confirmDialog.action || !record) return;

    try {
      setLoadingAction(confirmDialog.action);
      setConfirmDialog({ open: false, action: null });

      const endpoint =
        confirmDialog.action === 'refund'
          ? `${apiUrl}/refund/${record.id}`
          : `${apiUrl}/recharge/${record.id}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('auth') || '',
        },
      });

      if (response.ok) {
        notify(
          confirmDialog.action === 'refund'
            ? 'Refund initiated successfully'
            : 'Retry initiated successfully',
          { type: 'success' }
        );
        refresh();
      } else {
        const errorData = await response.json();
        notify(
          `${confirmDialog.action === 'refund' ? 'Refund' : 'Retry'} failed: ${
            errorData.message || 'Unknown error'
          }`,
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error(`${confirmDialog.action} error:`, error);
      notify(
        `${
          confirmDialog.action === 'refund' ? 'Refund' : 'Retry'
        } failed: Network error`,
        { type: 'error' }
      );
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: {
          xs: 'column-reverse',
          sm: 'column-reverse',
          md: 'row',
        },
        gap: 2,
      }}
    >
      <Box>
        <PrevNextButtons linkType='show' sort={{ field: 'createdAt', order: 'DESC' }} />
        <Card sx={{ mb: 10 }}>
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={8}>
                <Typography variant='h6' gutterBottom>
                  Bill Pay Order
                </Typography>
                <Grid container>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='billAmount' label='Bill Amount'>
                      <NumberField
                        source='billAmount'
                        options={{
                          style: 'currency',
                          currency: 'INR',
                        }}
                        sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='paidAmount' label='Paid Amount'>
                      <NumberField
                        source='paidAmount'
                        options={{
                          style: 'currency',
                          currency: 'INR',
                        }}
                        sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='createdAt' label='Placed On'>
                      <DateField
                        source='createdAt'
                        showTime
                        options={{
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour12: true,
                          hour: 'numeric',
                          minute: 'numeric',
                        }}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='id' label='Reference Id'>
                      <TextField source='id' />
                    </Labeled>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='paidOn' label='Paid At'>
                      <DateField
                        source='paidOn'
                        showTime
                        options={{
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour12: true,
                          hour: 'numeric',
                          minute: 'numeric',
                        }}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='clientInvoiceId' label='Invoice Id'>
                      {record?.clientInvoiceId &&
                      record?.billPayStatus === 'SUCCESS' ? (
                        <Link
                          target='_blank'
                          to={`https://paymadi.blr1.digitaloceanspaces.com/${record?.clientInvoiceId}.pdf`}
                        >
                          <TextField source='clientInvoiceId' />
                        </Link>
                      ) : (
                        <TextField emptyText='Not Available' />
                      )}
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='billerCategory'>
                      <TextField source='billerCategory' />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='billerName'>
                      <TextField source='billerName' />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Labeled source='bbpsRefId'>
                      <TextField source='bbpsRefId' />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Labeled source='billPayClientReferenceId'>
                      <TextField
                        source='billPayClientReferenceId'
                        emptyText='Not Available'
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Labeled source='billPayOrderId'>
                      <TextField
                        source='billPayOrderId'
                        emptyText='Not Available'
                      />
                    </Labeled>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Grid container>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='paymentStatus' label='Payment Status'>
                      <TextField
                        source='paymentStatus'
                        emptyText='Not Available'
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Labeled source='billPayStatus' label='Billpay Status'>
                      <TextField
                        source='billPayStatus'
                        emptyText='Not Available'
                      />
                    </Labeled>
                  </Grid>
                </Grid>

                {record?.billerCategory.toLowerCase() === 'mobile prepaid' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant='h6' gutterBottom>
                      Recharge Details
                    </Typography>
                    <Grid container>
                      <Grid item xs={12} sm={12} md={6}>
                        <Labeled source='pgName' label='Payment Gateway'>
                          <FunctionField
                            render={(record: any) =>
                              record?.pgDetail?.pgName || 'Not Available'
                            }
                          />
                        </Labeled>
                      </Grid>

                      <Grid item xs={12} sm={12} md={6}>
                        <Labeled source='mobileNo' label='Mobile nunber'>
                          <TextField source='mobileNo' />
                        </Labeled>
                      </Grid>

                      <Grid item xs={12} sm={12} md={6}>
                        <Labeled source='utrNo'>
                          <TextField source='utrNo' emptyText='Not Available' />
                        </Labeled>
                      </Grid>

                      {record?.billPayRefundId && (
                        <Grid item xs={12} sm={12} md={6}>
                          <Labeled source='billPayRefundId'>
                            <TextField source='billPayRefundId' />
                          </Labeled>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={12} md={6}>
                        <Labeled source='billerCategory'>
                          <TextField source='billerCategory' />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <Labeled
                          source='billerOperator'
                          label='Recharge Operator(Cyrus or A1TOPUP)'
                        >
                          <TextField source='billerOperator' emptyText='N/A' />
                        </Labeled>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <Labeled source='operatorName' label='Network Operator'>
                          <TextField
                            source='operatorName'
                            emptyText='Not Available'
                          />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Labeled source='circleName'>
                          <TextField
                            source='circleName'
                            emptyText='Not Available'
                          />
                        </Labeled>
                      </Grid>
                    </Grid>
                  </>
                )}

                {isRetryable && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction='row' spacing={2}>
                      <Button
                        variant='contained'
                        color='error'
                        disabled={loadingAction !== null}
                        onClick={() =>
                          setConfirmDialog({ open: true, action: 'refund' })
                        }
                        startIcon={
                          loadingAction === 'refund' ? (
                            <CircularProgress size={20} color='inherit' />
                          ) : (
                            <RefundIcon />
                          )
                        }
                      >
                        {loadingAction === 'refund'
                          ? 'Processing...'
                          : 'Refund'}
                      </Button>
                      <Button
                        variant='contained'
                        color='primary'
                        disabled={loadingAction !== null}
                        onClick={() =>
                          setConfirmDialog({ open: true, action: 'retry' })
                        }
                        startIcon={
                          loadingAction === 'retry' ? (
                            <CircularProgress size={20} color='inherit' />
                          ) : (
                            <RetryIcon />
                          )
                        }
                      >
                        {loadingAction === 'retry' ? 'Processing...' : 'Retry'}
                      </Button>
                    </Stack>
                  </>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Typography variant='h6' gutterBottom>
                  Customer
                </Typography>
                <ReferenceField
                  source='userId'
                  reference='customers'
                  link={false}
                >
                  <CustomerDetails />
                </ReferenceField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          Confirm {confirmDialog.action === 'refund' ? 'Refund' : 'Retry'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {confirmDialog.action === 'refund'
              ? `Are you sure you want to initiate a refund for ₹${record?.paidAmount}? This action cannot be undone.`
              : `Are you sure you want to retry this transaction for ₹${record?.billAmount}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null })}
            color='inherit'
            disabled={loadingAction !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'refund' ? 'error' : 'primary'}
            variant='contained'
            autoFocus
            disabled={loadingAction !== null}
            startIcon={
              loadingAction !== null ? (
                <CircularProgress size={20} color='inherit' />
              ) : null
            }
          >
            {loadingAction !== null
              ? 'Processing...'
              : confirmDialog.action === 'refund'
              ? 'Refund'
              : 'Retry'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default OrderEdit;
